import {useState, useSyncExternalStore} from 'react';
import {MutationFeedback, UiButton, UiCard, UiStatus} from '@morefunos/ui';
import type {ComboPricingRule} from '../../../packages/policy/business-policy.ts';
import type {AdminComboPort} from './admin-combo-port.ts';

const parseRequirements = (value: string): ComboPricingRule['requirements'] => {
  const selectorKeys = value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
  if (selectorKeys.length === 0) throw new Error('COMBO_REQUIREMENTS_REQUIRED');
  if (new Set(selectorKeys).size !== selectorKeys.length) throw new Error('COMBO_SELECTOR_DUPLICATE');
  return selectorKeys.map((selectorKey) => ({selectorKey, quantity: 1}));
};

const parseComponentPricing = (value: string): Readonly<Record<string, number>> => {
  const entries = value
    .split(/\r?\n/)
    .map((entry) => entry.trim())
    .filter(Boolean);
  if (entries.length === 0) throw new Error('COMBO_COMPONENT_PRICING_REQUIRED');

  const pricing: Record<string, number> = {};
  for (const entry of entries) {
    const separator = entry.indexOf('=');
    if (separator <= 0 || separator === entry.length - 1) throw new Error(`COMBO_COMPONENT_PRICING_INVALID:${entry}`);
    const productId = entry.slice(0, separator).trim();
    const amountText = entry.slice(separator + 1).trim();
    if (!productId || !/^\d+(?:\.\d{1,2})?$/.test(amountText)) throw new Error(`COMBO_COMPONENT_PRICING_INVALID:${entry}`);
    if (Object.prototype.hasOwnProperty.call(pricing, productId)) throw new Error(`COMBO_COMPONENT_DUPLICATE:${productId}`);
    const amountMinor = Math.round(Number(amountText) * 100);
    if (!Number.isSafeInteger(amountMinor) || amountMinor < 0) throw new Error(`COMBO_COMPONENT_PRICING_INVALID:${entry}`);
    pricing[productId] = amountMinor;
  }
  return pricing;
};

export function App({port}: {readonly port: AdminComboPort | null}) {
  if (!port) return <main className="admin-app admin-blocked"><MutationFeedback tone="critical" message="Admin Control Plane 未連接" detail="唔會建立假套餐設定或直接寫 live policy。" /></main>;
  return <Connected port={port} />;
}

function Connected({port}: {readonly port: AdminComboPort}) {
  const snapshot = useSyncExternalStore(port.subscribe, port.getSnapshot, port.getSnapshot);
  const [policyVersion, setPolicyVersion] = useState('');
  const [pricingRevision, setPricingRevision] = useState('');
  const [rulesRevision, setRulesRevision] = useState('');
  const [ruleId, setRuleId] = useState('');
  const [requirementsText, setRequirementsText] = useState('main, snack, drink');
  const [componentPricingText, setComponentPricingText] = useState('');
  const [message, setMessage] = useState('先建立草稿，唔會即時影響門店');

  const run = (action: () => unknown, success: string) => {
    try {
      action();
      setMessage(success);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '操作失敗');
    }
  };

  const createDraft = () => {
    const nextRule: ComboPricingRule = {
      ruleId: ruleId.trim(),
      enabled: true,
      priority: 100,
      requirements: parseRequirements(requirementsText),
      fixedBaseTotalMinor: 0,
      componentProductMinor: parseComponentPricing(componentPricingText),
    };
    const comboRules = [...snapshot.activeRules.filter((rule) => rule.ruleId !== nextRule.ruleId), nextRule];
    port.createDraft({policyVersion, pricingRevision, rulesRevision, comboRules});
  };

  const rulePricingLabel = (rule: ComboPricingRule): string => {
    const componentCount = rule.componentProductMinor ? Object.keys(rule.componentProductMinor).length : 0;
    return componentCount > 0 ? `組件價 ${componentCount} 項` : `Legacy $${(rule.fixedBaseTotalMinor / 100).toFixed(0)}`;
  };

  return <main className="admin-app">
    <header className="admin-header">
      <div><p className="admin-kicker">MoreFunOS Admin</p><h1>套餐規則</h1><p>Admin 發佈組件級套餐價格；SMT／SMM／客戶端只使用同一份 canonical quote。</p></div>
      <UiStatus tone={snapshot.activePolicyVersion ? 'positive' : 'critical'}>{snapshot.activePolicyVersion ?? '無 Active Policy'}</UiStatus>
    </header>

    <MutationFeedback tone="info" message={message} detail={snapshot.validationIssues.join(' · ') || undefined} />

    <section className="admin-grid">
      <UiCard className="admin-card">
        <h2>現行套餐規則</h2>
        {snapshot.activeRules.length === 0 ? <p className="admin-muted">目前未有套餐規則。</p> : snapshot.activeRules.map((rule) => <div className="admin-rule" key={rule.ruleId}>
          <strong>{rule.operationsLabel ?? rule.ruleId}</strong>
          <span>{rule.requirements.map((item) => `${item.selectorKey} ×${item.quantity}`).join(' + ')}</span>
          <span>{rulePricingLabel(rule)}</span>
        </div>)}
      </UiCard>

      <UiCard className="admin-card admin-editor">
        <h2>建立新版本草稿</h2>
        <label>Policy 版本<input value={policyVersion} onChange={(event) => setPolicyVersion(event.currentTarget.value)} placeholder="policy-v2" /></label>
        <label>Pricing revision<input value={pricingRevision} onChange={(event) => setPricingRevision(event.currentTarget.value)} placeholder="pricing-v2" /></label>
        <label>Rules revision<input value={rulesRevision} onChange={(event) => setRulesRevision(event.currentTarget.value)} placeholder="rules-v2" /></label>
        <label>套餐規則 ID<input value={ruleId} onChange={(event) => setRuleId(event.currentTarget.value)} placeholder="tier-c-meal" /></label>
        <label>需求組別<input value={requirementsText} onChange={(event) => setRequirementsText(event.currentTarget.value)} placeholder="main, snack, drink" /><small>以逗號分隔；每組目前需求 1 件。</small></label>
        <label>組件套餐價格<textarea rows={6} value={componentPricingText} onChange={(event) => setComponentPricingText(event.currentTarget.value)} placeholder={'riceball-c=45\nsalted-chicken=5\nhandmade-lemon-tea=10'} /><small>每行 productId=HK$。呢啲係套餐內組件金額，唔會改動單點價。</small></label>
        <UiButton variant="secondary" onPress={() => run(createDraft, '草稿已建立；live policy 未改')}>1. 建立草稿</UiButton>
      </UiCard>

      <UiCard className="admin-card admin-release">
        <h2>審核與生效</h2>
        <div><span>草稿</span><strong>{snapshot.draftId ?? '—'}</strong></div>
        <div><span>驗證</span><strong>{snapshot.validation}</strong></div>
        <div><span>已發佈</span><strong>{snapshot.publishedPolicyVersion ?? '—'}</strong></div>
        <UiButton variant="secondary" isDisabled={!snapshot.draftId} onPress={() => run(() => port.validateDraft(), '草稿驗證完成')}>2. 驗證草稿</UiButton>
        <UiButton variant="secondary" isDisabled={snapshot.validation !== 'valid'} onPress={() => run(() => port.publishDraft(), '新版本已發佈，但未啟用')}>3. 發佈版本</UiButton>
        <UiButton variant="primary" isDisabled={!snapshot.publishedPolicyVersion} onPress={() => run(() => port.activatePublished(), '新套餐 Policy 已正式啟用')}>4. 啟用版本</UiButton>
      </UiCard>
    </section>
  </main>;
}
